import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Power, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fcfa, formatApiError, resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { title: "", destination: "", type: "sejour", description: "", price_per_person: "", photos: [], departurePrices: [{ country: "", price: "" }], active: true };

export default function OffersAdmin() {
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/offers").then(({ data }) => setOffers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      title: o.title, destination: o.destination, type: o.type, description: o.description,
      price_per_person: o.price_per_person, photos: o.photos || [],
      departurePrices: (o.departure_prices && o.departure_prices.length ? o.departure_prices.map((d) => ({ country: d.country, price: d.price })) : [{ country: "", price: "" }]),
      active: o.active,
    });
    setOpen(true);
  };

  const uploadImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/offers/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.url] }));
      }
      toast.success("Image(s) ajoutée(s)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const setDp = (i, key, val) => setForm((f) => {
    const arr = [...f.departurePrices];
    arr[i] = { ...arr[i], [key]: val };
    return { ...f, departurePrices: arr };
  });
  const addDp = () => setForm((f) => ({ ...f, departurePrices: [...f.departurePrices, { country: "", price: "" }] }));
  const removeDp = (i) => setForm((f) => ({ ...f, departurePrices: f.departurePrices.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.photos.length) { toast.error("Ajoutez au moins une image"); return; }
    setSaving(true);
    const payload = {
      title: form.title,
      destination: form.destination,
      type: form.type,
      description: form.description,
      price_per_person: Number(form.price_per_person) || 0,
      photos: form.photos,
      available_dates: [],
      departure_prices: form.departurePrices
        .filter((d) => d.country.trim() && d.price !== "")
        .map((d) => ({ country: d.country.trim(), price: Number(d.price) || 0 })),
      active: form.active,
    };
    try {
      if (editing) await api.put(`/admin/offers/${editing.id}`, payload);
      else await api.post("/admin/offers", payload);
      toast.success(editing ? "Offre modifiée" : "Offre créée");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (o) => { await api.patch(`/admin/offers/${o.id}/toggle`).catch(() => {}); load(); };
  const remove = async (o) => {
    if (!window.confirm(`Supprimer « ${o.title} » ?`)) return;
    await api.delete(`/admin/offers/${o.id}`).catch(() => {});
    toast.success("Offre supprimée");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-head text-2xl font-extrabold text-primary">Offres</h2>
        <Button onClick={openNew} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="new-offer-btn">
          <Plus className="h-4 w-4 mr-1" /> Nouvelle offre
        </Button>
      </div>

      <div className="space-y-3">
        {offers.map((o) => (
          <div key={o.id} className="bg-white rounded-2xl soft-shadow border border-gray-100 p-4 flex items-center gap-4" data-testid={`admin-offer-${o.id}`}>
            <img src={resolveImg(o.photos?.[0])} alt="" className="h-16 w-16 rounded-xl object-cover bg-secondary" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-primary truncate">{o.title}</h3>
                {!o.active && <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Désactivée</span>}
              </div>
              <p className="text-xs text-muted-foreground">{o.destination} · dès {fcfa(o.price_per_person)} · {o.departure_prices?.length || 0} pays</p>
            </div>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" onClick={() => toggle(o)} data-testid={`toggle-offer-${o.id}`} title="Activer/Désactiver"><Power className={`h-4 w-4 ${o.active ? "text-green-600" : "text-gray-400"}`} /></Button>
              <Button size="icon" variant="ghost" onClick={() => openEdit(o)} data-testid={`edit-offer-${o.id}`}><Pencil className="h-4 w-4 text-primary" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(o)} data-testid={`delete-offer-${o.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="offer-dialog">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Modifier l'offre" : "Nouvelle offre"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Titre"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" data-testid="offer-title-input" /></Field>
            <Field label="Destination (ex. Zanzibar, Tanzanie)"><Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="rounded-xl" data-testid="offer-destination-input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full h-11 rounded-xl border border-input px-3 bg-white" data-testid="offer-type-select">
                  <option value="sejour">Séjour</option>
                  <option value="hotel">Hôtel</option>
                </select>
              </Field>
              <Field label="Prix de base « à partir de » (FCFA)"><Input type="number" value={form.price_per_person} onChange={(e) => setForm({ ...form, price_per_person: e.target.value })} className="rounded-xl" data-testid="offer-price-input" /></Field>
            </div>
            <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" rows={3} data-testid="offer-description-input" /></Field>

            <Field label="Images du lieu (téléversées depuis l'ordinateur)">
              <div className="flex flex-wrap gap-2 mb-2">
                {form.photos.map((p, i) => (
                  <div key={i} className="relative h-20 w-24 rounded-xl overflow-hidden border border-gray-200" data-testid={`offer-photo-${i}`}>
                    <img src={resolveImg(p)} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`remove-photo-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="h-20 w-24 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-accent text-muted-foreground" data-testid="offer-image-upload-label">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                  <span className="text-[10px] mt-1">Ajouter</span>
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple className="hidden" onChange={uploadImage} data-testid="offer-image-input" />
                </label>
              </div>
            </Field>

            <Field label="Prix par pays de départ">
              <div className="space-y-2">
                {form.departurePrices.map((d, i) => (
                  <div key={i} className="flex gap-2" data-testid={`dp-row-${i}`}>
                    <Input value={d.country} onChange={(e) => setDp(i, "country", e.target.value)} placeholder="Pays (ex. Cameroun)" className="rounded-xl" data-testid={`dp-country-${i}`} />
                    <Input type="number" value={d.price} onChange={(e) => setDp(i, "price", e.target.value)} placeholder="Prix FCFA" className="rounded-xl w-36" data-testid={`dp-price-${i}`} />
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeDp(i)} data-testid={`dp-remove-${i}`}><X className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addDp} className="rounded-full text-sm" data-testid="dp-add-btn"><Plus className="h-4 w-4 mr-1" /> Ajouter un pays</Button>
              </div>
            </Field>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving || uploading} className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="save-offer-btn">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-primary">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
